import { i18n } from 'i18n';
import {
  BackfillRecipeDto,
  FillType,
  FlushRecipeDto,
  FlushSpecificationDto,
  FlushVolumeMode,
  ProjectDto,
  RheologyDataDto,
  ThroughputControlType
} from 'providers/api';
import { updateRecipeState } from '../../../canvas/Model/updateRecipeState';
import { PasteProperties } from '../../../MineModel/Flushing/flushTypes';
import { Model } from '../../../hooks/useModelState';

export interface FlushFormValues {
  reference: string;
  backfillRecipeId: string;
  specification: FlushSpecificationDto;
}

export interface RecipePaste {
  paste: PasteProperties;
  wetFlowRate?: number;
}

interface RecipeMix {
  concentrationByMass: number;
  binderConcentration: number;
  throughput: number;
  throughputControlType: ThroughputControlType;
}

// The hydraulic recipe form falls back to this ratio when none is stored; the same value keeps the
// flush's mass balance consistent with what the recipe tab shows.
const DEFAULT_HYDRAULIC_BINDER_RATIO = 0.02;

const isFiniteNumber = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value);

const recipeMix = (recipe: BackfillRecipeDto, fillType: FillType): RecipeMix | undefined => {
  if (fillType === FillType.Paste) {
    const selected = recipe.pasteSelectedSpecification ?? recipe.pasteOptimisedSpecification;
    const specification = recipe.pasteSpecification;
    if (!selected || !specification) return undefined;
    return {
      concentrationByMass: selected.massConcentration / 100,
      binderConcentration: selected.binderContent / 100,
      throughput: specification.throughput,
      throughputControlType: specification.throughputControlType,
    };
  }

  const selected = recipe.hydraulicSelectedSpecification ?? recipe.hydraulicOptimisedSpecification;
  const specification = recipe.hydraulicSpecification;
  if (!selected || !specification) return undefined;
  return {
    concentrationByMass: selected.massConcentration / 100,
    binderConcentration: specification.binderTailingsRatio ?? DEFAULT_HYDRAULIC_BINDER_RATIO,
    throughput: specification.throughput,
    throughputControlType: specification.throughputControlType,
  };
};

// The paste ahead of the water front, as the linked recipe describes it, resolved through the same
// mass balance the recipe tab uses so the flush sees the density and flow rate the recipe was drawn with.
export const pasteFromRecipe = (model: Model, recipe: BackfillRecipeDto, rheology: RheologyDataDto): RecipePaste | undefined => {
  const mix = recipeMix(recipe, model.project.fillType);
  if (!mix) return undefined;

  const throughputFor = (type: ThroughputControlType) => (mix.throughputControlType === type ? mix.throughput : undefined);
  const state = updateRecipeState(
    model,
    {},
    mix.concentrationByMass,
    mix.binderConcentration,
    rheology.tailingsDrySolidsDensity,
    rheology.binderDryParticleDensity,
    rheology.carrierFluidDensity,
    throughputFor(ThroughputControlType.DryTonnage),
    throughputFor(ThroughputControlType.FlowRate),
    throughputFor(ThroughputControlType.WetTonnage),
    undefined,
    undefined,
    mix.throughputControlType,
  );
  if (!isFiniteNumber(state.wetDensity) || state.wetDensity <= 0) return undefined;

  return {
    paste: {
      wetDensity: state.wetDensity,
      concentrationByMass: mix.concentrationByMass,
      yieldStressA: rheology.yieldStressCoefficients.coefficient1,
      yieldStressB: rheology.yieldStressCoefficients.coefficient2,
      viscosityC: rheology.viscosityCoefficients.coefficient1,
      viscosityD: rheology.viscosityCoefficients.coefficient2,
      frictionLossFactorOfSafety: rheology.frictionLossFactorOfSafety,
    },
    wetFlowRate: isFiniteNumber(state.wetFlowRate) && state.wetFlowRate > 0 ? state.wetFlowRate : undefined,
  };
};

export const flushFormInitialValues = (
  project: ProjectDto,
  flushRecipe: FlushRecipeDto | undefined,
  defaultRecipeId: string | undefined,
  defaultPastePumpFlowRate: number | undefined,
): FlushFormValues => {
  if (flushRecipe) {
    return {
      reference: flushRecipe.reference,
      backfillRecipeId: flushRecipe.backfillRecipeId ?? '',
      specification: {
        ...flushRecipe.specification,
        airFlushMaxPressure: flushRecipe.specification.airFlushMaxPressure ?? undefined,
      },
    };
  }

  return {
    reference: '',
    backfillRecipeId: defaultRecipeId ?? '',
    specification: {
      flushPumpMaxPressure: project.flushPumpMaxPressure ?? 0,
      flushPumpFlowRate: project.flushPumpDefaultFlowRate ?? 0,
      pastePumpFlowRate: defaultPastePumpFlowRate ?? 0,
      airFlushEnabled: isFiniteNumber(project.airFlushMaxPressure),
      airFlushMaxPressure: project.airFlushMaxPressure ?? undefined,
      volumeMode: project.defaultFlushVolumeMode ?? FlushVolumeMode.PipeVolumeMultiple,
      volumeValue: project.defaultFlushVolumeValue ?? 1,
    },
  };
};

export const formatMinutes = (minutes: number): string => {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const remainder = whole % 60;
  if (hours === 0) {
    return i18n._('{minutes} min', { minutes: remainder });
  }
  return i18n._('{hours} h {minutes} min', { hours, minutes: remainder });
};
